<template>
  <div class="batch-job-detail flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header flex">
      <span class="a-header--title slide-down-enter-active">View Detail - {{ batchJobDetail.batchJobId }}</span>
      <a-space>
        <a-button size="default" class="a-header--button slide-down-enter-active" @click="downloadCsv"> <a-icon type="cloud-download" /> Download </a-button>
        <a-button size="default" class="a-header--button slide-down-enter-active" :loading="isCheckingBaselines" @click="onCheckBaselines"> <a-icon v-if="!isCheckingBaselines" type="file-search" /> Check Baseline </a-button>
        <a-tooltip title="Detects external third-party scripts and suggests policy rules to reduce false positives." placement="bottom">
          <a-button size="default" class="a-header--button slide-down-enter-active" :loading="isSuggestingPolicy" @click="onSuggestPolicy">
            <a-icon v-if="!isSuggestingPolicy" type="bulb" />
            Suggest Policy
          </a-button>
        </a-tooltip>
        <a-tag v-if="batchJobDetail.status == BatchJobStatus.Finished" style="height: 32px" class="a-header--button slide-down-enter-active" :color="BatchJobStatusColorTag.Finished">
          <span class="text-capitalize">{{ batchJobDetail.status }}</span>
        </a-tag>
        <a-tag v-if="batchJobDetail.status == BatchJobStatus.Cancelled" style="height: 32px" class="a-header--button slide-down-enter-active" :color="BatchJobStatusColorTag.Cancelled">
          <span class="text-capitalize">{{ batchJobDetail.status }}</span>
        </a-tag>
        <a-button v-if="batchJobDetail.status !== BatchJobStatus.Finished && batchJobDetail.status !== BatchJobStatus.Cancelled" size="default" class="a-header--button slide-down-enter-active" type="danger" @click="event => onCancelBatchJob(event)">
          Cancel batch job
        </a-button>
      </a-space>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto">
      <transition name="slide-up">
        <div class="info" v-if="!isLoading">
          <a-form>
            <a-row :gutter="24">
              <a-col :span="8">
                <a-form-item label="Batch ID">
                  <a-input :value="batchJobDetail.batchJobId" disabled></a-input>
                </a-form-item>
              </a-col>
              <a-col :span="8">
                <a-form-item label="Job Type">
                  <a-input :value="batchJobDetail.jobType" disabled></a-input>
                </a-form-item>
              </a-col>
              <a-col :span="8">
                <a-form-item label="Date/Time Added">
                  <a-input :value="batchJobDetail.dateAdded" disabled></a-input>
                </a-form-item>
              </a-col>
            </a-row>
          </a-form>
        </div>
      </transition>
      <div v-if="batchJobDetail.status !== BatchJobStatus.Finished && isCancelBatchJob">
        <a-alert showIcon type="error" class="mt-1" v-if="!isPageInQueue">
          <template slot="message"> No webpages still in queue. </template>
        </a-alert>
        <a-alert showIcon type="success" class="mt-1" v-if="isPageInQueue">
          <template slot="message"> Baselining for {{ countWebpageCanceled }} webpages cancelled. </template>
        </a-alert>
      </div>
      <transition name="slide-up">
        <div class="a-filter" v-if="!isLoading">
          <a-row class="full-w" type="flex" justify="start" :gutter="24">
            <a-col :span="8">
              <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                <a-button size="large">
                  <a-icon type="search" />
                </a-button>
                <a-input class="custom-prefix" placeholder="Search" @input="onSearchBatchJobBaseline"></a-input>
              </div>
            </a-col>
          </a-row>
        </div>
      </transition>
      <transition name="slide-up">
        <template v-if="!isLoading">
          <div v-resize="elSizeChange" class="a-table-data flex-grow-1 overflow-y-hidden">
            <PaginationTable :pagination="pagin" :columns="BatchJobDetailColumns" :dataSource="filterData" :tableSize="tableSize">
              <template slot="batchJobBaselineScreenShot" slot-scope="{ record }">
                <div class="screen-shot-container" @click="() => showModalScreenShot(record.batchJobBaselineScreenShot, record.webPageId)" v-if="record.batchJobBaselineScreenShot && record.isBatchJobBaselineSuccess">
                  <ImageItem class="article-item__image" :source="record.batchJobBaselineScreenShot" />
                </div>
              </template>
              <template slot="webPage" slot-scope="{ record }">
                <div v-if="record.isWebpageAvailable">
                  <a-input v-if="record.isBatchJobBaselineSuccess" :defaultValue="record.webPageName" placeholder="Unnamed page. Click and type to name" @blur="e => onBlurWebpageNameField(e.target.value, record.webPageName, record.webPageId)"></a-input>
                  <div class="mt-2 mb-1" v-if="record.webPageUrl">
                    <a-tooltip :title="record.webPageUrl" placement="left">
                      <a class="webpab-ext-link text-truncate" :href="record.webPageUrl" target="_blank" rel="noopener noreferrer">{{ record.webPageUrl }}</a>
                    </a-tooltip>
                  </div>
                  <div class="nav-link" v-if="record.isBatchJobBaselineSuccess">
                    <router-link :to="{ path: '/webpages/' + record.webPageId }">More configuration settings</router-link>
                  </div>
                  <div class="baseline-check-badge mt-2" v-if="baselineCheckComplete && record.batchJobBaselineStatus === BaselineStatus.Completed && baselineCheckResults[record.webPageId]">
                    <a-tag v-if="baselineCheckResults[record.webPageId].status === 'ok'" color="green">
                      <a-icon type="check-circle" /> Baseline Okay
                    </a-tag>
                    <a-tag v-else-if="baselineCheckResults[record.webPageId].status === 'issues'" color="orange">
                      <a-icon type="warning" /> {{ baselineCheckResults[record.webPageId].label }}
                    </a-tag>
                    <a-tag v-else-if="baselineCheckResults[record.webPageId].status === 'error'" color="gray">
                      <a-icon type="exclamation-circle" /> Check failed
                    </a-tag>
                  </div>
                </div>
                <div v-if="!record.isWebpageAvailable">
                  {{ record.webPageUrl }}
                </div>
              </template>
              <template slot="status" slot-scope="{ record }">
                <div class="status-container">
                  <a-tooltip :title="record.batchJobBaselineStatus">
                    <a-tag :color="record.batchJobBaselineStatus == BaselineStatus.Completed ? BaselineStatusColorTag.Completed : BaselineStatusColorTag.InCompleted" class="text-truncate d-inline-block text-center">
                      {{ isCancelBatchJob ? (record.batchJobBaselineStatus === BaselineStatus.InQueue ? BaselineStatus.Cancelled : record.batchJobBaselineStatus) : record.batchJobBaselineStatus }}
                    </a-tag>
                  </a-tooltip>
                  <a-tooltip class="mt-2" :title="record.batchJobBaselineFailedReason" v-if="record.batchJobBaselineFailedReason">
                    <a-tag :color="record.batchJobBaselineStatus == BaselineStatus.Completed ? BaselineStatusColorTag.Completed : BaselineStatusColorTag.InCompleted" class="text-truncate d-inline-block text-center">
                      {{ record.batchJobBaselineFailedReason }}
                    </a-tag>
                  </a-tooltip>
                </div>
              </template>
              <template slot="progress" slot-scope="{ record }">
                <div>
                  <a-tooltip :title="(record.batchJobBaselineProgress || 0) + '%'">
                    <div class="progress-container">
                      <div class="progress-success-segment" :class="{ 'not-available': !record.isWebpageAvailable }" :style="{ transform: 'translateX(-' + (100 - record.batchJobBaselineProgress) + '%)' }" />
                      <div class="progress-success-label" :style="{ transform: 'translateX(calc(-' + (100 - record.batchJobBaselineProgress) + '% + 10px))' }" v-if="record.batchJobBaselineProgress">{{ record.batchJobBaselineProgress }}%</div>
                    </div>
                  </a-tooltip>
                </div>
              </template>
            </PaginationTable>
          </div>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
    <a-modal v-model="visibleModal" style="top: 20px" cancelText="Close" :okButtonProps="{ style: { display: 'none' } }" :bodyStyle="{ padding: '10px' }" :width="modalViewType === 'html' ? 900 : 520" @ok="handleOk">
      <template slot="title">
        <a-radio-group v-model="modalViewType" size="small" button-style="solid">
          <a-radio-button value="screenshot">Screenshot</a-radio-button>
          <a-radio-button value="html">HTML</a-radio-button>
        </a-radio-group>
      </template>
      <img v-if="modalViewType === 'screenshot'" style="width: 100%; object-fit: contain" :src="imgSourceModal" alt="Batch job base line screen shot" />
      <PreviewHtml v-else-if="modalViewType === 'html' && modalWebPageId" :urlId="modalWebPageId" />
    </a-modal>
    <SuggestedPolicyModal
      :visible="showSuggestedPolicyModal"
      :groups="suggestedPolicyGroups"
      :allPolicies="allPoliciesForSuggestion"
      @close="showSuggestedPolicyModal = false"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash';
import ImageItem from '@shared/components/ImageItem';
import { PAGE_OPTIONS } from '@constants/pageOptions';
import capitalizeFilter from '@shared/filters/capitalize.filter';
import dateString from '@shared/filters/date-string.filter';
import timeString from '@shared/filters/time-string.filter';
import { API_URL } from '@shared/services/api.service';

import { BatchJobStatusColorTag, BatchJobStatus, BaselineStatusColorTag, BaselineStatus } from '../consts/BatchJob.consts';
import { BatchJobDetailColumns } from '../columnsTable/BatchJobColumns';

import BatchJobService from '../services/BatchJob.service';
import WebpageService from '../../Webpages/services/webpage.service';
import PreviewHtml from '../../Webpages/components/PreviewHtml.vue';
import { detectBaselineIssue } from '@shared/utils/baselinePatterns';
import SuggestedPolicyModal from '@shared/components/SuggestedPolicy/SuggestedPolicyModal.vue';
import suggestedPolicyMixin from '@shared/mixins/suggestedPolicy.mixin.js';

export default {
  name: 'BatchJobDetail',
  mixins: [suggestedPolicyMixin],
  components: {
    ImageItem,
    PreviewHtml,
    SuggestedPolicyModal,
  },
  data() {
    var pageSize = Number(window.localStorage.getItem('pageSize'));
    if (!pageSize) pageSize = 5;

    return {
      PAGE_OPTIONS,
      BatchJobStatus,
      BatchJobStatusColorTag,
      BaselineStatusColorTag,
      BaselineStatus,
      BatchJobDetailColumns,
      $_dataBatchJobBaseline: [],
      filterData: [],
      filterParams: {
        search: '',
      },
      batchJobDetail: {
        batchJobId: '',
        jobType: '',
        dateAdded: '',
        status: BatchJobStatus.Finished,
      },
      pagin: {
        current: 1,
        pageSize: pageSize,
        total: 0,
      },
      imgSourceModal: '',
      visibleModal: false,
      modalViewType: 'screenshot',
      modalWebPageId: null,
      isCancelBatchJob: false,
      countWebpageCanceled: 0,
      isPageInQueue: false,
      timeOutRefreshData: null,
      isLoading: true,
      tableSize: {
        height: '',
        width: '',
      },
      isCheckingBaselines: false,
      baselineCheckResults: {},
      baselineCheckComplete: false,
    };
  },
  beforeMount() {
    this.batchJobDetail.batchJobId = this.$route.params.id;
  },
  mounted() {
    this.fetchDetailBatchJob();
  },
  beforeDestroy() {
    clearTimeout(this.timeOutRefreshData);
  },
  methods: {
    getPagesForSuggestedPolicy() {
      return this.$_dataBatchJobBaseline.filter(
        item => item.batchJobBaselineStatus === BaselineStatus.Completed && item.webPageId
      );
    },
    getSuggestedPolicyEmptyMessage() {
      return 'No completed pages available for suggested policy.';
    },
    onSearchBatchJobBaseline: debounce(function (inputChangeEvent) {
      const inputSearchValue = inputChangeEvent.target.value;
      this.filterParams.search = (inputSearchValue && String(inputSearchValue).trim().toLowerCase()) || '';
      this.filter();
    }, 800),
    filter() {
      const filterResult = this.filterParams.search
        ? this.$_dataBatchJobBaseline.filter(item => {
            const filterByWebpageName = (item.webPageName || '').trim().toLowerCase().includes(this.filterParams.search);
            const filterByWebpageUrl = (item.webPageUrl || '').trim().toLowerCase().includes(this.filterParams.search);
            const filterByStatus = (item.batchJobBaselineStatus || '').trim().toLowerCase().includes(this.filterParams.search);
            return filterByWebpageName || filterByWebpageUrl || filterByStatus;
          })
        : this.$_dataBatchJobBaseline;
      this.filterData = filterResult;
      this.pagin = {
        current: 1,
        pageSize: this.pagin.pageSize,
        total: filterResult.length,
      };
    },
    async downloadCsv(e) {
      let link = document.createElement('a');
      let csvData = [];
      let csvHeader = ['No.', 'Webpage Name', 'URL', 'Baseline Status'];
      csvData.push(csvHeader);
      console.log(this.$_dataBatchJobBaseline);
      for (let i = 0; i < this.$_dataBatchJobBaseline.length; i++) {
        csvData.push([i + 1, this.$_dataBatchJobBaseline[i].webPageName, this.$_dataBatchJobBaseline[i].webPageUrl, this.$_dataBatchJobBaseline[i].batchJobBaselineStatus]);
      }
      // Format data into stringresponseData
      let csvDataStringRows = [];
      for (let row of csvData) {
        row = row.map(val => {
          if (val == null) {
            return '""';
          } else {
            let valReplacedSpecialChars = val
              .toString()
              .replace(/[\r\n\t]/gim, '')
              .replace('"', '""');
            return '"' + valReplacedSpecialChars + '"';
          }
        });
        csvDataStringRows.push(row.join(','));
      }
      let csvDataString = csvDataStringRows.join('\n');
      link.href = window.URL.createObjectURL(new Blob([csvDataString], { type: 'text/csv;charset=utf-8;' }));
      link.setAttribute('download', 'baseline.csv');
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }, 1000);
    },
    async fetchDetailBatchJob() {
      try {
        let isRefresh = false;
        const dataDetailBatchJob = await BatchJobService.fetchDetailsOfBatchBaselineJobById(this.batchJobDetail.batchJobId);
        const { id, start, entries, job_type, status } = dataDetailBatchJob;
        this.batchJobDetail = {
          ...this.batchJobDetail,
          status,
          batchJobId: id,
          jobType: job_type,
          dateAdded: `${dateString(start)} ${timeString(start)}`, // TODO:
        };
        this.$_dataBatchJobBaseline = (entries || []).map((item, index) => {
          const [status, reason] = String(item.status).split(', ');
          const itemStatus = status.trim();
          let itemReason = (reason && reason.trim()) || '';
          if (itemReason.startsWith('Reason')) {
            itemReason = itemReason
              .replace(/Reason/i, '')
              .trim()
              .replace(/:/, '')
              .trim();
          }
          itemReason = capitalizeFilter(itemReason);
          if (!isRefresh) {
            isRefresh = item.progress && item.progress < 100;
          }
          if (!this.isPageInQueue) {
            this.isPageInQueue = item.status === 'In Queue';
          }
          return {
            index: index + 1,
            batchJobBaselineScreenShot: item.url && item.url.baseline_screenshot ? `${API_URL}${item.url.baseline_screenshot.replace(/api\//i, '')}` : '',
            batchJobBaselineId: item.id,
            batchJobBaselineStatus: itemStatus,
            batchJobBaselineFailedReason: itemReason,
            batchJobBaselineProgress: item.progress,
            isBatchJobBaselineSuccess: !!item.success,
            isWebpageAvailable: !!item.url,
            webPageId: (item.url && item.url.id) || item.url_id,
            webPageName: item.url && item.url.name,
            webPageUrl: (item.url && item.url.url) || item.name,
          };
        });
        this.timeOutRefreshData = setTimeout(() => {
          if (isRefresh && status !== BatchJobStatus.Finished && status !== BatchJobStatus.Cancelled) {
            this.refreshProgressBatchJob();
          }
        }, 10000);
        this.filter();
      } catch {
        this.$router.push('/batch-jobs/list');
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        }
      }
    },
    async refreshProgressBatchJob() {
      let isRefresh = false;
      const dataDetailBatchJob = await BatchJobService.fetchDetailsOfBatchBaselineJobById(this.batchJobDetail.batchJobId);
      const { entries, status } = dataDetailBatchJob;
      this.batchJobDetail.status = status;
      const dataBatchJobBaseline = (entries || []).map(item => {
        const [status, reason] = String(item.status).split('Reason:');
        const itemStatus = status.trim();
        let itemReason = (reason && reason.trim()) || '';
        if (itemReason.startsWith('Reason')) {
          itemReason = itemReason
            .replace(/Reason/i, '')
            .trim()
            .replace(/:/, '')
            .trim();
        }
        itemReason = capitalizeFilter(itemReason);
        if (!isRefresh) {
          isRefresh = item.progress && item.progress < 100;
        }
        if (!this.isPageInQueue) {
          this.isPageInQueue = item.status === 'In Queue';
        }
        return {
          batchJobBaselineId: item.id,
          batchJobBaselineScreenShot: item.url && item.url.baseline_screenshot ? `${API_URL}${item.url.baseline_screenshot.replace(/api\//i, '')}` : '',
          batchJobBaselineStatus: itemStatus,
          batchJobBaselineFailedReason: itemReason,
          batchJobBaselineProgress: item.progress,
          isBatchJobBaselineSuccess: !!item.success,
          isWebpageAvailable: !!item.url,
          webPageId: (item.url && item.url.id) || item.url_id,
          webPageName: item.url && item.url.name,
          webPageUrl: (item.url && item.url.url) || item.name,
        };
      });
      const cloneCurrentDataBatJobBaseline = [...this.$_dataBatchJobBaseline];
      cloneCurrentDataBatJobBaseline.forEach(baselineItem => {
        const newValue = dataBatchJobBaseline.find(item => item.batchJobBaselineId === baselineItem.batchJobBaselineId);
        if (newValue) {
          baselineItem.batchJobBaselineStatus = newValue.batchJobBaselineStatus;
          baselineItem.batchJobBaselineFailedReason = newValue.batchJobBaselineFailedReason;
          baselineItem.batchJobBaselineProgress = newValue.batchJobBaselineProgress;
          baselineItem.isBatchJobBaselineSuccess = newValue.isBatchJobBaselineSuccess;
          baselineItem.isWebpageAvailable = newValue.isWebpageAvailable;
          baselineItem.webPageId = newValue.webPageId;
          baselineItem.webPageName = newValue.webPageName;
          baselineItem.webPageUrl = newValue.webPageUrl;
          baselineItem.batchJobBaselineScreenShot = newValue.batchJobBaselineScreenShot;
        }
      });
      this.$_dataBatchJobBaseline = cloneCurrentDataBatJobBaseline;
      this.filter();
      if (isRefresh && status !== BatchJobStatus.Finished && status !== BatchJobStatus.Cancelled) {
        this.timeOutRefreshData = setTimeout(() => {
          this.refreshProgressBatchJob();
        }, 10000);
      }
    },
    paginChange(pagin) {
      this.pagin = { ...pagin };
    },
    showModalScreenShot(batchJobBaselineScreenShotSource, webPageId) {
      this.imgSourceModal = batchJobBaselineScreenShotSource;
      this.modalWebPageId = webPageId;
      this.modalViewType = 'screenshot';
      this.visibleModal = true;
    },
    handleOk() {
      this.visibleModal = false;
    },
    async onBlurWebpageNameField(newWebpageName, oldWebpageName, webPageId) {
      if (newWebpageName !== oldWebpageName) {
        try {
          const webPageData = await WebpageService.get(webPageId);
          const newData = { ...webPageData, config_name: newWebpageName };
          await WebpageService.updateUrlConfig(webPageId, newData);
        } catch {
          this.$showErrorMessage('Error when update webpage name');
        }
      }
    },
    async onCancelBatchJob(event) {
      event.stopPropagation();
      this.$confirm({
        title: 'Are you sure you want to cancel this batch job? Note that only webpages that are till in queue will be cancelled.',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          if (this.isPageInQueue) {
            const { cancelled } = await BatchJobService.cancelBatchJob(this.batchJobDetail.batchJobId);
            this.countWebpageCanceled = cancelled.length;
          }
          this.isCancelBatchJob = true;
        },
      });
    },
    elSizeChange(size) {
      this.tableSize = size;
    },
    async onCheckBaselines() {
      this.isCheckingBaselines = true;
      this.baselineCheckResults = {};
      
      // Filter to only completed pages with valid webPageId because others will obviously fail
      const completedPages = this.$_dataBatchJobBaseline.filter(
        item => item.batchJobBaselineStatus === BaselineStatus.Completed && item.webPageId
      );
      
      if (completedPages.length === 0) {
        this.$message.info('No completed pages to check.');
        this.isCheckingBaselines = false;
        this.baselineCheckComplete = true;
        return;
      }
      
      const resultsMap = {};
      let issueCount = 0, okCount = 0, errorCount = 0;
      
      try {
        // Batch fetch all baseline HTML in a single request 
        const urlIds = completedPages.map(page => page.webPageId);
        const response = await WebpageService.getBaselineHtml(urlIds);
        const batchResults = response.results || {};
        
        // Process each result and check for baseline issues
        for (const urlId of urlIds) {
          const pageResult = batchResults[urlId];
          if (pageResult && pageResult.html) {
            const issueResult = detectBaselineIssue(pageResult.html);
            if (issueResult.hasIssues) {
              resultsMap[urlId] = { status: 'issues', label: issueResult.label };
              issueCount++;
            } else {
              resultsMap[urlId] = { status: 'ok' };
              okCount++;
            }
          } else {
            resultsMap[urlId] = { status: 'error' };
            errorCount++;
          }
        }
      } catch (err) {
        // If batch request fails, mark all as error
        errorCount = completedPages.length;
        completedPages.forEach(page => {
          resultsMap[page.webPageId] = { status: 'error' };
        });
      }
      
      this.baselineCheckResults = resultsMap;
      this.baselineCheckComplete = true;
      this.isCheckingBaselines = false;
      
      if (issueCount > 0) {
        this.$message.warning(`Check complete: ${issueCount} page(s) might have issues, ${okCount} OK${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      } else {
        this.$message.success(`Check complete: All ${okCount} page(s) OK${errorCount > 0 ? `, ${errorCount} failed to check` : ''}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.text-capitalize {
  text-transform: capitalize;
}
.webpab-ext-link {
  display: block;
  color: inherit;
  &:hover {
    color: #20a4b8;
    text-decoration: underline;
  }
}
.nav-link {
  a {
    text-decoration: underline;
  }
}
.baseline-check-badge {
  .ant-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}
.a-body {
  .info {
    background: #f7f7f7;
    padding: 0 20px;
    border-radius: 4px;
  }
  .a-filter {
    padding-left: 0;
    padding-right: 0;
  }
  .a-table-data {
    min-height: 200px;
    margin-left: -20px;
    margin-right: -20px;
  }
}
.a-table-data {
  .screen-shot-container {
    cursor: pointer;
    object-fit: contain;
    overflow-y: hidden;
    width: 100%;
    height: auto;
    max-height: 150px;
  }
}
.status-container {
  display: block;
  max-width: inherit;
  span {
    max-width: inherit;
  }
}
.progress-container {
  position: relative;
  overflow-x: hidden;
  height: 12px;
  width: 100%;
  border-radius: 6px;
  background-color: #e9e9e9;
  .progress-success-segment {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: 6px;
    background-color: #71ba3b;
    &.not-available {
      background-color: #a2a2a2;
    }
  }
  .progress-success-label {
    position: absolute;
    z-index: 2;
    top: -2px;
    left: 0;
    height: 100%;
    width: 50%;
    background-color: transparent;
    font-size: 8px;
    line-height: 15px;
    color: #ffffff;
    text-align: right;
  }
}
::v-deep {
  .ant-table-column-has-actions {
    vertical-align: top;
  }
}
</style>
